import { LubooDialog } from './luboo-dialog.js';
import { createInviteIdentityField } from './invite-identity-field.js';
import { setupChannel, destroyChannel, lubooRequest } from '../mixins/data-channel.mixin.js';

/**
 * <property-permissions-list> — property team management UI (#287).
 *
 * Renders every account member with their effective property role
 * (explicit from UserProperty, or inherited from AccountRole). Drives
 * the prop-settings → Permissions sub-view. Fetches on connectedCallback
 * via GET /api/accounts/{aid}/properties/{pid}/team?include_inherited=true.
 *
 * @attr {string} account-id - Account UUID (required)
 * @attr {string} property-id - Property UUID (required)
 * @attr {boolean} can-manage - Whether the viewer can change roles (server-
 *   gated via PropertyPermission.MANAGE_PROPERTY_TEAM). Read-only mode hides
 *   the per-row action controls.
 */

const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
  viewer: 'Viewer',
};

const ACCOUNT_ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

class PropertyPermissionsList extends HTMLElement {
  static get observedAttributes() {
    return ['account-id', 'property-id', 'can-manage'];
  }

  // Mixin methods attached as instance fields (esbuild ordering safe;
  // see CLAUDE.md "Component registration (Pattern A)" notes).
  setupChannel = setupChannel;
  destroyChannel = destroyChannel;

  // Cross-tab sync. The merged include_inherited=true view requires the
  // server to resolve inheritance after a mutation — handlers refetch
  // rather than try to recompute "explicit vs inherited" client-side.
  _messageMap = {
    property_member_assigned: this._onPropertyMemberAssigned,
    property_member_updated: this._onPropertyMemberUpdated,
    property_member_removed: this._onPropertyMemberRemoved,
  };

  constructor() {
    super();
    this._initialized = false;
    this._members = [];
    this._propertyRoles = []; // system + custom roles for this property (#287)
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    // Use document-level delegation so listeners survive pane moves /
    // re-attach when the workspace clones the view into a new pane.
    // Filter to events that originated inside this element via _scopedClick.
    document.addEventListener('click', this._scopedClick = (e) => {
      if (this.contains(e.target)) this._onClick(e);
    });
    document.addEventListener('change', this._scopedChange = (e) => {
      if (this.contains(e.target)) this._onChange(e);
    });
    if (!this._initialized) {
      this._initialized = true;
      this._renderLoading();
    }
    if (this.accountId && this.propertyId) {
      this.setupChannel(`luboo:property:${this.propertyId}`);
      this.refresh();
    }
  }

  disconnectedCallback() {
    if (this._scopedClick) document.removeEventListener('click', this._scopedClick);
    if (this._scopedChange) document.removeEventListener('change', this._scopedChange);
    this._scopedClick = null;
    this._scopedChange = null;
    this.destroyChannel();
  }

  _onClick(e) {
    if (!this.canManage) return;
    const overrideBtn = e.target.closest(
      '[data-property-permissions-action="override"]'
    );
    if (overrideBtn) {
      e.preventDefault();
      const row = overrideBtn.closest('[data-property-permissions-row]');
      if (!row) return;
      const userId = row.getAttribute('data-user-id');
      this._openOverrideDialog(userId);
      return;
    }
    const revertBtn = e.target.closest(
      '[data-property-permissions-action="revert"]'
    );
    if (revertBtn) {
      e.preventDefault();
      const row = revertBtn.closest('[data-property-permissions-row]');
      if (!row) return;
      const userId = row.getAttribute('data-user-id');
      this._confirmRevert(userId);
      return;
    }
    if (e.target.closest('[data-property-permissions-action="invite-member"]')) {
      e.preventDefault();
      this._openInviteDialog();
    }
  }

  async _openInviteDialog() {
    // Fetch live property roles so custom property roles appear too.
    let roles = [];
    try {
      roles = await lubooRequest(
        `luboo:account:${this.accountId}`,
        'list_roles',
        { accountId: this.accountId, propertyId: this.propertyId },
        { group: 'property_permission' },
      );
    } catch {}

    const dlg = document.createElement('luboo-dialog');
    dlg.setAttribute('heading', 'Invite member to this property');

    const intro = document.createElement('p');
    intro.textContent =
      "We'll send an invitation email to join the account at Viewer (lowest) level, plus apply the property role you pick below as an explicit override. The invitee will only have access at this property.";
    dlg.appendChild(intro);

    const identityField = createInviteIdentityField({
      parent: dlg,
      existingMemberIds: this._members.map((m) => m.user_id),
    });

    const roleLabel = document.createElement('label');
    roleLabel.style.display = 'block';
    roleLabel.style.marginTop = '0.5rem';
    const roleSpan = document.createElement('span');
    roleSpan.textContent = 'Role at this property';
    roleLabel.appendChild(roleSpan);
    const roleSelect = document.createElement('select');
    roleSelect.className = 'dialog-input';

    let defaultRoleId = roles.find(
      (r) => r.is_system && r.system_kind === 'staff'
    )?.id;
    if (!defaultRoleId && roles.length) defaultRoleId = roles[0].id;
    if (roles.length) {
      for (const r of roles) {
        const opt = document.createElement('option');
        opt.value = r.id;
        const tag = r.is_system ? '' : ' (custom)';
        opt.textContent = `${r.name}${tag}`;
        opt.dataset.systemKind = r.system_kind || '';
        opt.dataset.isSystem = String(!!r.is_system);
        if (r.id === defaultRoleId) opt.selected = true;
        roleSelect.appendChild(opt);
      }
    } else {
      // API failure fallback — use the system enum labels so the dialog
      // is still usable; the server treats them as the property_role enum.
      for (const [value, label] of Object.entries(ROLE_LABELS)) {
        const opt = document.createElement('option');
        opt.value = value;
        opt.textContent = label;
        opt.dataset.systemKind = value;
        opt.dataset.isSystem = 'true';
        if (value === 'staff') opt.selected = true;
        roleSelect.appendChild(opt);
      }
    }

    roleLabel.appendChild(roleSelect);
    dlg.appendChild(roleLabel);

    const cancelBtn = document.createElement('button');
    cancelBtn.setAttribute('slot', 'actions');
    cancelBtn.setAttribute('value', 'cancel');
    cancelBtn.textContent = 'Cancel';
    dlg.appendChild(cancelBtn);

    const sendBtn = document.createElement('button');
    sendBtn.setAttribute('slot', 'actions');
    sendBtn.setAttribute('value', 'confirm');
    sendBtn.setAttribute('type', 'submit');
    sendBtn.textContent = 'Send invite';
    dlg.appendChild(sendBtn);

    document.body.appendChild(dlg);

    let identity = null;
    let selectedOpt = null;
    try {
      const result = await dlg.open();
      if (result !== 'confirm') return;
      const err = identityField.validate();
      if (err) {
        await LubooDialog.alert(err, { heading: 'Invite failed' });
        return;
      }
      identity = identityField.getIdentity();
      selectedOpt = roleSelect.options[roleSelect.selectedIndex];
    } finally {
      if (typeof dlg._destroy === 'function') dlg._destroy();
    }
    if (!identity || !selectedOpt) return;

    const isSystem = selectedOpt.dataset.isSystem === 'true';
    const systemKind = selectedOpt.dataset.systemKind;
    const payload = {
      ...identity,
      role: 'viewer', // Option A: account role forced to lowest by server
      property_id: this.propertyId,
    };
    if (isSystem && systemKind) {
      payload.property_role = systemKind; // legacy enum, matches PropertyRole.value
    } else {
      // Custom property role — send FK. Server validates it belongs to this property.
      payload.property_role = 'viewer'; // fallback enum (lowest); property_role_id is authoritative
      payload.property_role_id = selectedOpt.value;
    }

    try {
      await lubooRequest(
        `luboo:account:${this.accountId}`,
        'invite',
        {
          accountId: this.accountId,
          body: {
            ...payload,
            invite_url_template: `${window.location.origin}/invite/{token}`,
          },
        },
        { group: 'member' },
      );
    } catch (err) {
      await LubooDialog.alert(err.detail || err.message || 'Could not create invitation.', {
        heading: 'Invite failed',
      });
      return;
    }
    await LubooDialog.alert(
      "Invitation email sent. Once the recipient accepts, they'll appear here with the role you assigned.",
      { heading: 'Invite sent' }
    );
    await this.refresh();
  }

  _onChange(e) {
    if (!this.canManage) return;
    const select = e.target.closest(
      'select[data-property-permissions-action="change-role"]'
    );
    if (select) {
      const row = select.closest('[data-property-permissions-row]');
      if (!row) return;
      const userId = row.getAttribute('data-user-id');
      const opt = select.options[select.selectedIndex];
      this._submitChange(userId, opt, select);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this._initialized) return;
    if (oldValue === newValue) return;
    if (name === 'account-id' || name === 'property-id') {
      if (name === 'property-id' && this.propertyId) {
        // Re-subscribe to the new property's channel so cross-tab events
        // for the previous property stop firing.
        this.setupChannel(`luboo:property:${this.propertyId}`);
      }
      this.refresh();
    }
  }

  get accountId() {
    return this.getAttribute('account-id');
  }

  get propertyId() {
    return this.getAttribute('property-id');
  }

  get canManage() {
    return this.getAttribute('can-manage') === 'true';
  }

  /**
   * Fetch the latest team and re-render. Public so other code paths
   * (refresh-after-mutation, polling) can trigger a refresh.
   */
  async refresh() {
    if (!this.accountId || !this.propertyId) return;
    this._renderLoading();
    try {
      const channel = `luboo:account:${this.accountId}`;
      const [members, roles] = await Promise.all([
        lubooRequest(
          channel,
          'get_team',
          { accountId: this.accountId, propertyId: this.propertyId, includeInherited: true },
          { group: 'property_permission' },
        ),
        // Roles fetch is best-effort — tolerate failure (e.g. permission
        // gate not satisfied for non-managers).
        lubooRequest(
          channel,
          'list_roles',
          { accountId: this.accountId, propertyId: this.propertyId },
          { group: 'property_permission' },
        ).catch(() => null),
      ]);
      this._members = Array.isArray(members) ? members : [];
      this._propertyRoles = Array.isArray(roles) ? roles : [];
      this._renderMembers();
    } catch (err) {
      this._renderError(err);
    }
  }

  // ── Cross-tab sync handlers ──────────────────────────────────────────
  //
  // The merged "include_inherited=true" view collapses each user into a
  // single row whose role + source depend on whether a UserProperty exists.
  // Recomputing that locally from a bare assigned/updated/removed payload
  // would mean duplicating the server-side inheritance logic in JS. Refetch
  // instead — it's a single HTTP round-trip per cross-tab event, which is
  // identical to the post-mutation refresh() the local tab already does.

  _onPropertyMemberAssigned(payload) {
    // Server scope match: payload carries property_id so we can ignore
    // stale events that arrive after a property switch.
    if (!payload?.property_id || payload.property_id !== this.propertyId) return;
    this.refresh();
  }

  _onPropertyMemberUpdated(payload) {
    if (!payload?.property_id || payload.property_id !== this.propertyId) return;
    this.refresh();
  }

  _onPropertyMemberRemoved(payload) {
    if (!payload?.property_id || payload.property_id !== this.propertyId) return;
    this.refresh();
  }

  // ── Rendering ─────────────────────────────────────────────────────────

  _renderLoading() {
    this.innerHTML =
      '<div class="property-permissions-loading luboo-empty-state">' +
      '<svg-icon icon="luboo:loader-droplet"></svg-icon>' +
      '<p>Loading team…</p>' +
      '</div>';
  }

  _renderError(err) {
    const msg = this._escape(err && err.message ? err.message : String(err));
    this.innerHTML =
      '<div class="property-permissions-error luboo-empty-state">' +
      '<svg-icon icon="luboo:alert-triangle"></svg-icon>' +
      '<h3>Couldn\'t load team</h3>' +
      `<p>${msg}</p>` +
      '</div>';
  }

  _renderMembers() {
    const toolbar = this.canManage
      ? `<div class="property-permissions-toolbar">
          <button class="btn btn-primary"
                  data-property-permissions-action="invite-member">
            <svg-icon icon="luboo:user-plus"></svg-icon> Invite member
          </button>
        </div>`
      : '';
    if (!this._members.length) {
      this.innerHTML =
        toolbar +
        '<div class="property-permissions-empty luboo-empty-state">' +
        '<svg-icon icon="luboo:users"></svg-icon>' +
        '<h3>No team members yet</h3>' +
        '<p>Invite users below.</p>' +
        '</div>';
      return;
    }
    const rows = this._members.map((m) => this._rowHtml(m)).join('');
    this.innerHTML = toolbar + `<div class="property-permissions-rows" role="list">${rows}</div>`;
  }

  _rowHtml(member) {
    const role = member.role || 'viewer';
    const roleLabel = ROLE_LABELS[role] || role;
    const isExplicit = member.inheritance_source === 'explicit';

    // Inheritance source line: "Inherited from Account Owner" or "Explicit"
    let sourceLabel = 'Explicit';
    if (!isExplicit) {
      const accountRole = (member.inheritance_source || '').split(':')[1] || '';
      const accountRoleLabel = ACCOUNT_ROLE_LABELS[accountRole] || accountRole;
      sourceLabel = `Inherited from Account ${accountRoleLabel}`;
    }

    const displayName = this._displayName(member);
    const subtitle = member.email && displayName !== member.email ? member.email : '';

    return (
      `<div class="property-permissions-row" role="listitem" ` +
      `data-property-permissions-row ` +
      `data-user-id="${this._escape(member.user_id)}" ` +
      `data-inheritance-source="${this._escape(member.inheritance_source || '')}">` +
      `<div class="property-permissions-row-avatar">` +
      `<topographic-identicon-or-image ` +
      `username="${this._escape(member.username || member.email || member.user_id || displayName)}" ` +
      (member.avatar_url ? `url="${this._escape(member.avatar_url)}" ` : '') +
      `num-peaks="4" num-contours="12" ` +
      `peak-height-max="0.9" peak-spread="23" ` +
      `peak-spacing="1" stroke-width="0.9" ` +
      `show-peaks="false" ` +
      `bg-color="var(--panel-color)" ` +
      `line-color="var(--text-color)" ` +
      `accent-color="var(--highlight-color)"` +
      `></topographic-identicon-or-image>` +
      `</div>` +
      `<div class="property-permissions-row-info">` +
      `<div class="property-permissions-row-name">${this._escape(displayName)}</div>` +
      (subtitle
        ? `<div class="property-permissions-row-email">${this._escape(subtitle)}</div>`
        : '') +
      `</div>` +
      `<div class="property-permissions-row-role">` +
      `<span class="property-permissions-role-badge property-permissions-role-${this._escape(role)}">${this._escape(roleLabel)}</span>` +
      `<span class="property-permissions-source ${isExplicit ? 'is-explicit' : 'is-inherited'}">${this._escape(sourceLabel)}</span>` +
      `</div>` +
      `<div class="property-permissions-row-actions">` +
      this._actionsHtml(member, isExplicit) +
      `</div>` +
      `</div>`
    );
  }

  _actionsHtml(member, isExplicit) {
    if (!this.canManage) return '';
    if (!isExplicit) {
      return (
        `<button class="btn btn-sm btn-secondary" ` +
        `data-property-permissions-action="override" ` +
        `aria-label="Override role for ${this._escape(this._displayName(member))}">` +
        `<svg-icon icon="luboo:edit"></svg-icon> Override` +
        `</button>`
      );
    }
    // Explicit row: inline role <select> for fast role changes, plus a
    // Revert button that deletes the UserProperty so the user falls back
    // to their inherited account role. The dropdown lists system + custom
    // property roles fetched in refresh() — when no roles are available
    // (API failure), fall back to the legacy system enum.
    const options = this._buildRoleOptions(member);
    const displayName = this._escape(this._displayName(member));
    return (
      `<select class="property-permissions-role-inline" ` +
      `data-property-permissions-action="change-role" ` +
      `aria-label="Change role for ${displayName}">` +
      options +
      `</select>` +
      `<button class="btn btn-sm btn-secondary property-permissions-revert" ` +
      `data-property-permissions-action="revert" ` +
      `aria-label="Revert ${displayName} to inherited role" ` +
      `title="Revert to inherited role">` +
      `<svg-icon icon="luboo:undo"></svg-icon>` +
      `</button>`
    );
  }

  /**
   * Build <option> HTML for the per-row role dropdown.
   *
   * Selection rule: prefer match by member.role_id (covers custom roles
   * AND system roles since we now seed role_id on UserProperty); fall
   * back to system-kind matching member.role enum when role_id is null
   * (legacy rows pre-#287).
   */
  _buildRoleOptions(member) {
    const roles = this._propertyRoles || [];
    if (!roles.length) {
      return Object.entries(ROLE_LABELS)
        .map(
          ([value, label]) =>
            `<option value="${this._escape(value)}"` +
            ` data-role-kind="system"` +
            ` data-system-kind="${this._escape(value)}"` +
            (value === member.role ? ' selected' : '') +
            `>${this._escape(label)}</option>`
        )
        .join('');
    }
    const memberRoleId = member.role_id || '';
    const memberRoleEnum = (member.role || '').toString();
    return roles
      .map((r) => {
        const isSystem = !!r.is_system;
        const tag = isSystem ? '' : ' (custom)';
        const label = `${r.name}${tag}`;
        // Select rule: match role_id first, else match by system_kind
        // against the enum value when role_id is missing.
        let selected = false;
        if (memberRoleId) {
          selected = r.id === memberRoleId;
        } else if (isSystem && r.system_kind === memberRoleEnum) {
          selected = true;
        }
        return (
          `<option value="${this._escape(r.id)}"` +
          ` data-role-kind="${isSystem ? 'system' : 'custom'}"` +
          ` data-system-kind="${this._escape(r.system_kind || '')}"` +
          (selected ? ' selected' : '') +
          `>${this._escape(label)}</option>`
        );
      })
      .join('');
  }

  // ── Override dialog (Phase 3) ────────────────────────────────────────

  async _openOverrideDialog(userId) {
    const member = this._members.find((m) => m.user_id === userId);
    if (!member) return;
    const displayName = this._displayName(member);
    const inheritedRoleLabel =
      ROLE_LABELS[member.role] || member.role || 'no role';

    const dlg = document.createElement('luboo-dialog');
    dlg.setAttribute('heading', `Override role: ${displayName}`);

    const p1 = document.createElement('p');
    p1.textContent =
      `${displayName} currently inherits ${inheritedRoleLabel} from their ` +
      'account role. Override with an explicit role for this property:';
    dlg.appendChild(p1);

    const select = document.createElement('select');
    select.className = 'dialog-input property-permissions-role-select';
    select.setAttribute('aria-label', 'New property role');
    select.innerHTML = this._buildRoleOptions(member);
    dlg.appendChild(select);

    const cancelBtn = document.createElement('button');
    cancelBtn.setAttribute('slot', 'actions');
    cancelBtn.setAttribute('value', 'cancel');
    cancelBtn.textContent = 'Cancel';
    dlg.appendChild(cancelBtn);

    const saveBtn = document.createElement('button');
    saveBtn.setAttribute('slot', 'actions');
    saveBtn.setAttribute('value', 'confirm');
    saveBtn.setAttribute('type', 'submit');
    saveBtn.textContent = 'Save Override';
    dlg.appendChild(saveBtn);

    document.body.appendChild(dlg);

    try {
      const result = await dlg.open();
      if (result !== 'confirm') return;
      const opt = select.options[select.selectedIndex];
      await this._submitOverride(userId, opt);
    } finally {
      if (typeof dlg._destroy === 'function') dlg._destroy();
    }
  }

  // ── Revert to inherited (Phase 5) ────────────────────────────────────

  async _confirmRevert(userId) {
    const member = this._members.find((m) => m.user_id === userId);
    if (!member) return;
    const displayName = this._displayName(member);
    const ok = await LubooDialog.confirm(
      `Revert ${displayName} to their inherited account role? This removes the explicit ${ROLE_LABELS[member.role] || member.role} assignment.`,
      {
        heading: `Revert ${displayName}`,
        okLabel: 'Revert',
        cancelLabel: 'Cancel',
      }
    );
    if (!ok) return;
    await this._submitRevert(userId);
  }

  async _submitRevert(userId) {
    try {
      await lubooRequest(
        `luboo:account:${this.accountId}`,
        'revert',
        { accountId: this.accountId, propertyId: this.propertyId, userId },
        { group: 'property_permission' },
      );
      window.LubooToast?.success('Reverted to inherited role');
      await this.refresh();
    } catch (err) {
      window.LubooToast?.error(err.message || 'Failed to revert role');
    }
  }

  // ── Change role (Phase 4) ────────────────────────────────────────────

  /**
   * Translate the dropdown <option> into a {role, role_id?} payload.
   * System options carry `data-role-kind="system"` + `data-system-kind`;
   * custom options carry `data-role-kind="custom"` + the role's id in
   * `option.value`. Returns null for malformed options.
   */
  _payloadFromOption(opt) {
    if (!opt) return null;
    const kind = opt.dataset.roleKind;
    const systemKind = opt.dataset.systemKind;
    if (kind === 'custom') {
      const enumFallback =
        systemKind && ROLE_LABELS[systemKind] ? systemKind : 'viewer';
      return { role: enumFallback, role_id: opt.value };
    }
    // system path — `value` may be the role's id (when the API returned
    // roles) or the enum string (legacy fallback). Prefer system_kind
    // when present; else fall back to the option value.
    return { role: systemKind || opt.value };
  }

  _labelFromOption(opt) {
    if (!opt) return '';
    return opt.text || ROLE_LABELS[opt.dataset.systemKind] || opt.value;
  }

  async _submitChange(userId, opt, selectEl) {
    const payload = this._payloadFromOption(opt);
    if (!payload) {
      await this.refresh();
      return;
    }
    try {
      selectEl.disabled = true;
      await lubooRequest(
        `luboo:account:${this.accountId}`,
        'set_role',
        { accountId: this.accountId, propertyId: this.propertyId, userId, body: payload },
        { group: 'property_permission' },
      );
      window.LubooToast?.success(`Role updated to ${this._labelFromOption(opt)}`);
      await this.refresh();
    } catch (err) {
      window.LubooToast?.error(err.message || 'Failed to update role');
      // refresh anyway so the select snaps back to the server's value
      await this.refresh();
    }
  }

  async _submitOverride(userId, opt) {
    const payload = this._payloadFromOption(opt);
    if (!payload) return;
    try {
      await lubooRequest(
        `luboo:account:${this.accountId}`,
        'override_role',
        { accountId: this.accountId, propertyId: this.propertyId, userId, body: payload },
        { group: 'property_permission' },
      );
      window.LubooToast?.success(
        `Role overridden to ${this._labelFromOption(opt)}`
      );
      await this.refresh();
    } catch (err) {
      window.LubooToast?.error(err.message || 'Failed to override role');
    }
  }

  _displayName(member) {
    const parts = [member.first_name, member.last_name].filter(Boolean);
    if (parts.length) return parts.join(' ');
    return member.username || member.email || member.user_id || '(unknown)';
  }

  _escape(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

export { PropertyPermissionsList };
